// Kolam-inspired SVG patterns for decorative use
export function KolamCorner({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="10" cy="10" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="30" cy="10" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="50" cy="10" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="10" cy="30" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="30" cy="30" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="10" cy="50" r="3" fill="currentColor" opacity="0.3" />
      <path
        d="M10 10 Q20 20 30 10 Q40 20 50 10"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
      />
      <path
        d="M10 10 Q20 20 10 30 Q20 40 10 50"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
      />
      <path
        d="M10 30 Q20 30 30 30 Q30 20 30 10"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
      />
    </svg>
  );
}

export function KolamDivider({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 400 40"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Center lotus-like pattern */}
      <circle cx="200" cy="20" r="4" fill="currentColor" opacity="0.5" />
      <circle cx="180" cy="20" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="220" cy="20" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="200" cy="8" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="200" cy="32" r="2" fill="currentColor" opacity="0.3" />

      {/* Curved lines forming lotus petals */}
      <path
        d="M200 20 Q190 10 180 20 Q190 30 200 20"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.4"
      />
      <path
        d="M200 20 Q210 10 220 20 Q210 30 200 20"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.4"
      />
      <path
        d="M200 20 Q190 15 200 8 Q210 15 200 20"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.4"
      />
      <path
        d="M200 20 Q190 25 200 32 Q210 25 200 20"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.4"
      />

      {/* Extending lines */}
      <line x1="160" y1="20" x2="100" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.2" />
      <line x1="240" y1="20" x2="300" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.2" />

      {/* Dots along the lines */}
      <circle cx="140" cy="20" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="120" cy="20" r="1.5" fill="currentColor" opacity="0.2" />
      <circle cx="260" cy="20" r="2" fill="currentColor" opacity="0.3" />
      <circle cx="280" cy="20" r="1.5" fill="currentColor" opacity="0.2" />
    </svg>
  );
}

export function KolamBorder({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 100 20"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <pattern id="kolamDots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
        <circle cx="10" cy="10" r="2" fill="currentColor" opacity="0.2" />
      </pattern>
      <rect width="100" height="20" fill="url(#kolamDots)" />
      <path
        d="M0 10 Q10 5 20 10 Q30 15 40 10 Q50 5 60 10 Q70 15 80 10 Q90 5 100 10"
        stroke="currentColor"
        strokeWidth="1"
        opacity="0.3"
        fill="none"
      />
    </svg>
  );
}

export function KolamMandala({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Center */}
      <circle cx="100" cy="100" r="8" fill="currentColor" opacity="0.3" />

      {/* Inner ring of dots */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <circle
          key={angle}
          cx={100 + 25 * Math.cos((angle * Math.PI) / 180)}
          cy={100 + 25 * Math.sin((angle * Math.PI) / 180)}
          r="4"
          fill="currentColor"
          opacity="0.25"
        />
      ))}

      {/* Middle ring of dots */}
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
        <circle
          key={`mid-${angle}`}
          cx={100 + 50 * Math.cos((angle * Math.PI) / 180)}
          cy={100 + 50 * Math.sin((angle * Math.PI) / 180)}
          r="3"
          fill="currentColor"
          opacity="0.2"
        />
      ))}

      {/* Outer ring of dots */}
      {[0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5, 180, 202.5, 225, 247.5, 270, 292.5, 315, 337.5].map((angle) => (
        <circle
          key={`outer-${angle}`}
          cx={100 + 80 * Math.cos((angle * Math.PI) / 180)}
          cy={100 + 80 * Math.sin((angle * Math.PI) / 180)}
          r="2"
          fill="currentColor"
          opacity="0.15"
        />
      ))}

      {/* Connecting curves - lotus petals */}
      {[0, 90, 180, 270].map((angle) => (
        <path
          key={`petal-${angle}`}
          d={`M100 100 Q${100 + 40 * Math.cos(((angle - 30) * Math.PI) / 180)} ${100 + 40 * Math.sin(((angle - 30) * Math.PI) / 180)} ${100 + 50 * Math.cos((angle * Math.PI) / 180)} ${100 + 50 * Math.sin((angle * Math.PI) / 180)} Q${100 + 40 * Math.cos(((angle + 30) * Math.PI) / 180)} ${100 + 40 * Math.sin(((angle + 30) * Math.PI) / 180)} 100 100`}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.2"
        />
      ))}
    </svg>
  );
}
